import { useEffect, useState } from 'react';
import {
  fmtUsd,
  lastFullMonths,
  MAX_UPLOAD_BYTES,
  SOF_DOC_KINDS,
  SOF_EXPECTED_BANDS,
  SOF_INCOME_BANDS,
  SOF_SOURCES,
  statementCoverageError,
  type SofDeclaration,
  type SofDocKind,
  type SofDocument,
  type SofState,
  type SofStatus,
} from '@platform/shared';
import { cx, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import { useLoad, useVersion } from '../../stores/account';
import { AIcon, Alert, Card, Chip, errMsg, Field, H2, Notice, PillButton, when } from './kit';

const ACCEPT = 'image/jpeg,image/png,application/pdf';
const monthName = (ym: string) => new Date(`${ym}-15T00:00:00Z`).toLocaleDateString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const dayName = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const kindLabel = (k: SofDocKind) => SOF_DOC_KINDS.find((x) => x.id === k)!.label;
const covers = (d: SofDocument, ym: string) => d.kind === 'bank_statement' && !!d.periodFrom && d.periodFrom <= ym && (d.periodTo ?? d.periodFrom) >= ym;

const STATUS: Record<SofStatus, { label: string; tone: 'up' | 'down' | 'warn' | 'accent' | 'muted' }> = {
  not_required: { label: 'Not required yet', tone: 'muted' },
  required: { label: 'Required', tone: 'warn' },
  pending: { label: 'Under review', tone: 'accent' },
  approved: { label: 'Approved', tone: 'up' },
  rejected: { label: 'Rejected', tone: 'down' },
  expired: { label: 'Expired', tone: 'warn' },
};

async function uploadDoc(fields: Record<string, string>, f: File): Promise<SofState> {
  if (f.size > MAX_UPLOAD_BYTES) throw new Error('That file is larger than 10 MB');
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);
  fd.append('file', f, f.name);
  return api.upload<SofState>('/api/sof/documents', fd);
}

/** One upload target: a month of statements, all three months in one file, or a supporting document. */
function Slot({
  label,
  hint,
  doc,
  fields,
  disabled,
  onState,
  onRemove,
}: {
  label: string;
  hint: string;
  doc?: SofDocument;
  fields: Record<string, string>;
  disabled: boolean;
  onState: (s: SofState) => void;
  onRemove?: (id: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const rejected = doc?.status === 'rejected';
  const ok = doc && !rejected;
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    setErr(null);
    try {
      onState(await uploadDoc(fields, f));
    } catch (x) {
      setErr(x instanceof Error && !('status' in x) ? x.message : errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={cx('flex items-center gap-4 rounded-[20px] border p-4 md:p-5', rejected ? 'border-down' : ok ? 'border-line' : 'border-dashed border-line-strong')}>
      <span className={cx('grid size-11 shrink-0 place-items-center rounded-full', ok ? 'bg-up-bg text-up' : rejected ? 'bg-down-bg text-down' : 'bg-track')}>
        {busy ? <Spinner /> : <AIcon name={ok ? 'check' : rejected ? 'x' : 'upload'} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px]">{label}</span>
        <span className={cx('block truncate text-[14px]', err || rejected ? 'text-down' : 'text-muted')}>
          {err ?? (rejected ? 'Rejected. Upload a new one.' : doc ? doc.filename : hint)}
        </span>
      </span>
      {!disabled && (
        <span className="flex shrink-0 items-center gap-4">
          {doc && onRemove && (doc.status === 'uploaded' || rejected) && (
            <button type="button" className="text-[15px] font-semibold text-muted hover:text-down" onClick={() => onRemove(doc.id)}>
              Remove
            </button>
          )}
          <label className="cursor-pointer text-[15px] font-semibold">
            {doc ? 'Replace' : 'Upload'}
            <input type="file" accept={ACCEPT} className="hidden" disabled={busy} onChange={(e) => void pick(e.target.files?.[0])} />
          </label>
        </span>
      )}
    </div>
  );
}

const EMPTY: Partial<SofDeclaration> = {};

export function SourceOfFundsTab() {
  const v = useVersion('compliance');
  const { data: state, error, setData } = useLoad<SofState>('/api/sof', [v]);
  const [form, setForm] = useState<Partial<SofDeclaration>>(EMPTY);
  const [extraKind, setExtraKind] = useState<SofDocKind>('payslip');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const latest = state?.latest;
  useEffect(() => {
    if (latest && form === EMPTY) setForm(latest);
  }, [latest, form]);

  if (error) return <Notice msg={{ ok: false, text: error }} />;
  if (!state) return <Spinner />;

  const c = state.compliance;
  const s = state.status;
  const editable = s !== 'pending' && s !== 'approved';
  const months = lastFullMonths(new Date());
  const docs = state.documents;
  const statements = docs.filter((d) => d.kind === 'bank_statement');
  const extras = docs.filter((d) => d.kind !== 'bank_statement');
  const usable = statements.filter((d) => d.status !== 'rejected').map((d) => ({ from: d.periodFrom!, to: d.periodTo ?? d.periodFrom! }));
  const gap = statementCoverageError(usable, new Date());
  const kycReady = state.kycStatus === 'approved' || state.kycStatus === 'pending';
  const set = <K extends keyof SofDeclaration>(k: K, val: SofDeclaration[K]) => setForm((f) => ({ ...f, [k]: val }));
  const declared = !!form.source && !!form.occupation?.trim() && !!form.incomeBand && !!form.expectedBand && (form.source !== 'other' || !!form.sourceDetails?.trim());
  const progress = Math.min(100, (Number(c.lifetimeDeposits) / Number(c.threshold)) * 100);

  const remove = async (id: string) => {
    setMsg(null);
    try {
      setData(await api.del<SofState>(`/api/sof/documents/${id}`));
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    }
  };
  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setData(await api.post<SofState>('/api/sof/submit', form));
      setMsg({ ok: true, text: 'Submitted. We usually reply within 1 business day.' });
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };

  const heroText =
    s === 'approved'
      ? `Your source of funds is approved${latest?.validUntil ? ` until ${dayName(latest.validUntil)}` : ''}. Deposits and withdrawals have no extra limits.`
      : s === 'pending'
        ? 'We are reviewing your documents and will be in touch shortly.'
        : s === 'rejected'
          ? 'Some documents need to be replaced before we can approve your source of funds.'
          : c.sofRequired
            ? `Your deposits have passed ${fmtUsd(c.threshold, 0)}.${c.enforced ? ' Withdrawals are paused until we approve where your funds come from.' : ' Please send us your documents.'}`
            : `Once your total deposits pass ${fmtUsd(c.threshold, 0)} we need to know where your funds come from. You can send your documents now to avoid any pause later.`;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <div className="flex min-w-0 flex-col gap-8">
        <section className="rounded-[24px] bg-hero p-5 text-hero-fg md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="m-0 text-[22px] font-semibold md:text-[26px]">Source of funds</h2>
            <Chip tone={STATUS[s].tone}>
              {STATUS[s].label}
              {c.sofRequired && c.sofDueAt && s !== 'pending' ? ` · due ${dayName(c.sofDueAt)}` : ''}
            </Chip>
          </div>
          <p className="m-0 mt-3 text-[16px] text-hero-muted md:text-[17px]">{heroText}</p>
          {s !== 'approved' && (
            <div className="mt-6 flex flex-col gap-2">
              <div className="flex justify-between gap-3 text-[14px] text-hero-muted">
                <span>Total deposits</span>
                <span className="num">
                  {fmtUsd(c.lifetimeDeposits)} / {fmtUsd(c.threshold, 0)}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-hero-line">
                <i className={cx('block h-full rounded-full', c.sofRequired ? 'bg-[#f5a524]' : 'bg-white')} style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </section>

        {!kycReady && (
          <Alert>
            Verify your identity first; we check your statements against your verified name and address.{' '}
            <Link to="/account/kyc" className="font-semibold text-fg">
              Go to KYC →
            </Link>
          </Alert>
        )}
        {s === 'rejected' && latest?.rejectReason && (
          <Alert>
            Your documents were rejected{latest.reviewedAt ? ` on ${when(latest.reviewedAt, true)}` : ''}: <b>“{latest.rejectReason}”</b> Replace the{' '}
            {latest.rejectedKinds.map((k) => kindLabel(k).toLowerCase() + 's').join(', ')} and submit again. Your other documents are kept.
          </Alert>
        )}

        {editable ? (
          <Card className="flex flex-col gap-8">
            <div className="flex flex-col gap-5">
              <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">1. About your funds</h3>
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Main source" htmlFor="sof-source">
                  <select id="sof-source" className="field" value={form.source ?? ''} onChange={(e) => set('source', e.target.value as SofDeclaration['source'])}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {SOF_SOURCES.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Occupation" htmlFor="sof-occ">
                  <input id="sof-occ" className="field" maxLength={200} placeholder="e.g. Civil engineer" value={form.occupation ?? ''} onChange={(e) => set('occupation', e.target.value)} />
                </Field>
                <Field
                  label={
                    <>
                      Employer or business <span className="font-normal text-muted">(optional)</span>
                    </>
                  }
                  htmlFor="sof-emp"
                >
                  <input id="sof-emp" className="field" maxLength={200} value={form.employer ?? ''} onChange={(e) => set('employer', e.target.value)} />
                </Field>
                <Field label="Annual income" htmlFor="sof-inc">
                  <select id="sof-inc" className="field" value={form.incomeBand ?? ''} onChange={(e) => set('incomeBand', e.target.value)}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {SOF_INCOME_BANDS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Expected deposits, next 12 months" htmlFor="sof-exp">
                  <select id="sof-exp" className="field" value={form.expectedBand ?? ''} onChange={(e) => set('expectedBand', e.target.value)}>
                    <option value="" disabled>
                      Choose…
                    </option>
                    {SOF_EXPECTED_BANDS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  label={
                    <>
                      Details <span className="font-normal text-muted">{form.source === 'other' ? '' : '(optional)'}</span>
                    </>
                  }
                  htmlFor="sof-det"
                  className="md:col-span-2"
                >
                  <textarea id="sof-det" className="field min-h-[88px] py-3" maxLength={1000} value={form.sourceDetails ?? ''} onChange={(e) => set('sourceDetails', e.target.value)} />
                </Field>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">2. Bank statements, last 3 months</h3>
                <span className="text-[14px] text-muted">In your name · latest must end within 45 days</span>
              </div>
              <div className="grid gap-3">
                {months.map((m) => (
                  <Slot
                    key={m}
                    label={monthName(m)}
                    hint="Statement for this month"
                    doc={statements.find((d) => covers(d, m))}
                    fields={{ kind: 'bank_statement', periodFrom: m, periodTo: m }}
                    disabled={false}
                    onState={setData}
                    onRemove={(id) => void remove(id)}
                  />
                ))}
              </div>
              <Slot
                label="One file for all 3 months"
                hint={`${monthName(months[0]!)} – ${monthName(months[2]!)} in a single PDF`}
                fields={{ kind: 'bank_statement', periodFrom: months[0]!, periodTo: months[2]! }}
                disabled={false}
                onState={setData}
              />
              <p className="m-0 text-[14px] text-muted">PDF from your bank is best. JPG, PNG or PDF, up to 10 MB each.</p>
            </div>

            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">
                3. Supporting documents <span className="text-[15px] font-normal text-muted">(optional)</span>
              </h3>
              {extras.map((d) => (
                <Slot key={d.id} label={kindLabel(d.kind)} hint="" doc={d} fields={{ kind: d.kind }} disabled={false} onState={setData} onRemove={(id) => void remove(id)} />
              ))}
              <div className="flex flex-wrap items-center gap-3">
                <select aria-label="Document type" className="field !w-auto" value={extraKind} onChange={(e) => setExtraKind(e.target.value as SofDocKind)}>
                  {SOF_DOC_KINDS.filter((k) => k.id !== 'bank_statement').map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.label}
                    </option>
                  ))}
                </select>
                <AddExtra kind={extraKind} onState={setData} onError={(t) => setMsg({ ok: false, text: t })} />
              </div>
            </div>

            {gap && statements.length > 0 && <p className="m-0 text-[15px] text-warn">{gap}</p>}
            <Notice msg={msg} onClose={() => setMsg(null)} />
            <PillButton size="lg" className="sm:w-max" busy={busy} disabled={!declared || !!gap || !kycReady} onClick={() => void submit()}>
              {s === 'rejected' ? 'Submit again' : 'Submit for review'}
            </PillButton>
          </Card>
        ) : (
          <section className="flex flex-col gap-4">
            <Notice msg={msg} />
            <H2>Submitted documents</H2>
            <table className="w-full border-collapse text-[15px] md:text-[17px]">
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-b border-line">
                    <td className="px-2 py-5 md:px-6">
                      {kindLabel(d.kind)}
                      {d.periodFrom && <span className="text-muted"> · {d.periodFrom === d.periodTo ? monthName(d.periodFrom) : `${monthName(d.periodFrom)} – ${monthName(d.periodTo!)}`}</span>}
                    </td>
                    <td className="truncate px-3 text-muted max-sm:hidden">{d.filename}</td>
                    <td className="px-2 text-right md:px-6">{d.status === 'accepted' ? <Chip tone="up">Accepted</Chip> : <Chip tone="accent">Reviewing</Chip>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      <Card className="flex flex-col gap-4">
        <H2 className="!text-[22px]">Why we ask</H2>
        <p className="m-0 text-[16px] leading-relaxed text-muted">
          Anti-money-laundering rules require us to understand where larger deposits come from. Above {fmtUsd(c.threshold, 0)} in total deposits we review your bank statements once, and again after 12 months.
        </p>
        <ul className="m-0 flex list-none flex-col gap-3 p-0 text-[16px]">
          {['Trading stays open while we review', 'A recent statement can also count as your proof of address', 'Files are encrypted in transit and seen only by our compliance team'].map((t) => (
            <li key={t} className="flex gap-3">
              <AIcon name="check" className="shrink-0 text-up" />
              {t}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function AddExtra({ kind, onState, onError }: { kind: SofDocKind; onState: (s: SofState) => void; onError: (t: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className={cx('inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-line-strong px-5 text-[15px] font-semibold', busy && 'opacity-60')}>
      {busy ? <Spinner /> : <AIcon name="upload" size={16} />}
      Add file
      <input
        type="file"
        accept={ACCEPT}
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setBusy(true);
          uploadDoc({ kind }, f)
            .then(onState)
            .catch((x) => onError(x instanceof Error && !('status' in x) ? x.message : errMsg(x)))
            .finally(() => setBusy(false));
        }}
      />
    </label>
  );
}
